import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { PressurePoint } from '../../models/external-pressure-profile.model';
import { LinearInterpSingleVal } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';

export function createCustomEppColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'measuredDepth',
      header: 'MD',
      unitKey: 'longLengths',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    },
    {
      kind: 'number',
      field: 'pressure',
      header: 'Pressure',
      unitKey: 'pressure',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    }
  ];
}

export function createCustomEppRow(data?: Partial<PressurePoint>): FormGroup {
  return new FormGroup({
    measuredDepth: new FormControl<number | null>(data?.measuredDepth ?? null, [Validators.required]),
    pressure: new FormControl<number | null>(data?.pressure ?? null, [Validators.required])
  });
}

export function isCustomEppRowEmpty(row: FormGroup): boolean {
  const md = row.get('measuredDepth')?.value;
  const pressure = row.get('pressure')?.value;
  const isMdEmpty = md === null || md === undefined || md === '' || Number.isNaN(Number(md));
  const isPressureEmpty = pressure === null || pressure === undefined || pressure === '' || Number.isNaN(Number(pressure));
  return isMdEmpty && isPressureEmpty;
}

// Keeps a single trailing blank row so users can add rows by typing, without needing the context menu.
export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createCustomEppRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isCustomEppRowEmpty(formArray.at(formArray.length - 1)) &&
    isCustomEppRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  if (!isCustomEppRowEmpty(formArray.at(formArray.length - 1))) {
    formArray.push(createCustomEppRow());
  }
}

export function populateCustomEppFormArray(
  formArray: FormArray<FormGroup>,
  points: readonly PressurePoint[]
): void {
  formArray.clear();
  points.forEach((point) => formArray.push(createCustomEppRow(point)));
  ensureTrailingInputRow(formArray);
}

export function extractCustomEppRows(formArray: FormArray<FormGroup>): PressurePoint[] {
  return formArray.controls
    .filter((control) => !isCustomEppRowEmpty(control as FormGroup))
    .map((control) => ({
      measuredDepth: Number(control.get('measuredDepth')?.value ?? 0),
      pressure: Number(control.get('pressure')?.value ?? 0)
    }));
}

function toNullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

// Unlike extractCustomEppRows, keeps missing values as null instead of 0 so they aren't used for interpolation.
export function extractCustomEppPoints(rows: readonly FormGroup[]): PressurePoint[] {
  return rows
    .filter((row) => !isCustomEppRowEmpty(row))
    .map((row) => ({
      measuredDepth: toNullableNumber(row.get('measuredDepth')?.value),
      pressure: toNullableNumber(row.get('pressure')?.value)
    }));
}

function compareByMeasuredDepth(a: PressurePoint, b: PressurePoint): number {
  const aHasDepth = Number.isFinite(a.measuredDepth);
  const bHasDepth = Number.isFinite(b.measuredDepth);
  if (aHasDepth && bHasDepth) {
    return a.measuredDepth - b.measuredDepth;
  }
  return Number(bHasDepth) - Number(aHasDepth);
}

// Ensures every packer has a point at its depth and one just above it, interpolated from the profile.
export function addCustomEppPackerPoints(
  points: readonly PressurePoint[],
  packerDepths: readonly number[],
  depthAbovePacker: number,
  tolerance = 0.01
): PressurePoint[] {
  const profile = points
    .filter(p => Number.isFinite(p.measuredDepth) && Number.isFinite(p.pressure))
    .sort(compareByMeasuredDepth);

  if (profile.length === 0) {
    return [...points].sort(compareByMeasuredDepth);
  }

  const depths = profile.map(p => p.measuredDepth);
  const pressures = profile.map(p => p.pressure);
  const hasPointNear = (depth: number): boolean =>
    points.some(p => Number.isFinite(p.measuredDepth) && Math.abs(p.measuredDepth - depth) <= tolerance);

  const packerPoints = packerDepths
    .flatMap(depth => [depth, depth - depthAbovePacker])
    .filter(depth => !hasPointNear(depth))
    .map(depth => ({
      measuredDepth: +depth,
      pressure: +LinearInterpSingleVal(depth, depths, pressures).toFixed(3)
    }));

  return [...points, ...packerPoints]
    .sort(compareByMeasuredDepth)
    .filter((p, i, arr) =>
      i === 0
      || !Number.isFinite(p.measuredDepth)
      || Math.abs(p.measuredDepth - arr[i - 1].measuredDepth) > 0.0001
    );
}

export function findRowIndexNearDepth(
  formArray: FormArray<FormGroup>,
  depth: number,
  tolerance = 0.0001
): number {
  return formArray.controls.findIndex((control) => {
    const value = control.get('measuredDepth')?.value;
    return value !== null && value !== undefined && value !== '' && Math.abs(Number(value) - depth) < tolerance;
  });
}

export function isCustomEppPackerDepthRow(
  row: FormGroup,
  packerDepths: readonly number[],
  tolerance = 0.01
): boolean {
  const measuredDepth = row.get('measuredDepth')?.value;
  return measuredDepth !== null
    && measuredDepth !== undefined
    && measuredDepth !== ''
    && packerDepths.some(packerDepth => Math.abs(packerDepth - Number(measuredDepth)) < tolerance);
}

export interface CustomEppPackerNameDepth {
  readonly name: string;
  readonly depth: number;
}

interface CustomEppPackerRowContext {
  readonly relation: 'packer' | 'above';
  readonly packerName: string;
}

function getCustomEppPackerRowContext(
  rows: readonly FormGroup[],
  rowIndex: number,
  packerDepths: readonly number[],
  packerNameDepths: readonly CustomEppPackerNameDepth[],
  tolerance: number
): CustomEppPackerRowContext | null {
  const row = rows[rowIndex];
  if (!row) {
    return null;
  }

  const packerName = getPackerNameForRow(row, packerDepths, packerNameDepths, tolerance);
  if (packerName) {
    return { relation: 'packer', packerName };
  }

  const nextRow = rows[rowIndex + 1];
  if (!nextRow) {
    return null;
  }

  const nextPackerName = getPackerNameForRow(nextRow, packerDepths, packerNameDepths, tolerance);
  return nextPackerName ? { relation: 'above', packerName: nextPackerName } : null;
}

export function isCustomEppProtectedRow(
  rows: readonly FormGroup[],
  rowIndex: number,
  packerDepths: readonly number[],
  packerNameDepths: readonly CustomEppPackerNameDepth[],
  tolerance = 0.01
): boolean {
  return getCustomEppPackerRowContext(rows, rowIndex, packerDepths, packerNameDepths, tolerance) !== null;
}

// Labels the packer row itself; the companion row above it falls through to the disabled delete icon.
export function getCustomEppRowActionLabel(
  rows: readonly FormGroup[],
  rowIndex: number,
  packerDepths: readonly number[],
  packerNameDepths: readonly CustomEppPackerNameDepth[],
  tolerance = 0.01
): string | null {
  const context = getCustomEppPackerRowContext(rows, rowIndex, packerDepths, packerNameDepths, tolerance);
  if (!context) {
    return null;
  }

  return context.relation === 'packer' ? `Packer: ${context.packerName}` : null;
}

export function getCustomEppRowActionIconSrc(
  rows: readonly FormGroup[],
  rowIndex: number,
  packerDepths: readonly number[],
  packerNameDepths: readonly CustomEppPackerNameDepth[],
  tolerance = 0.01
): string | null {
  return getCustomEppPackerRowContext(rows, rowIndex, packerDepths, packerNameDepths, tolerance)?.relation === 'packer'
    ? '../../assets/icons/packers-24x24.svg'
    : null;
}

// Matches ng-table-grid's per-cell pTooltip wording ("Depth/Pressure Below/Above - {name}").
export function getCustomEppCellTooltip(
  rows: readonly FormGroup[],
  rowIndex: number,
  columnField: string,
  packerDepths: readonly number[],
  packerNameDepths: readonly CustomEppPackerNameDepth[],
  tolerance = 0.01
): string | null {
  const context = getCustomEppPackerRowContext(rows, rowIndex, packerDepths, packerNameDepths, tolerance);
  if (!context) {
    return null;
  }

  const valueLabel = columnField === 'measuredDepth' ? 'Depth' : 'Pressure';
  const relationLabel = context.relation === 'packer' ? 'Below' : 'Above';
  return `${valueLabel} ${relationLabel} - ${context.packerName}`;
}

function getPackerNameForRow(
  row: FormGroup,
  packerDepths: readonly number[],
  packerNameDepths: readonly CustomEppPackerNameDepth[],
  tolerance: number
): string | null {
  if (!isCustomEppPackerDepthRow(row, packerDepths, tolerance)) {
    return null;
  }

  const measuredDepth = Number(row.get('measuredDepth')?.value);
  return packerNameDepths.find(p => Math.abs(p.depth - measuredDepth) <= tolerance)?.name ?? 'Packer';
}

